import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useEffect, useId, useRef, useState } from "react";
import { useAppPreferences } from "../lib/preferences";

export interface HardConfirmRequest {
  confirmationValue: string;
  confirmButtonLabel: string;
  confirmFieldLabel?: string;
  consequence?: string;
  description: string;
  title: string;
}

type PendingRequest = HardConfirmRequest & {
  resolve: (value: boolean) => void;
};

function normalizeConfirmationValue(value: string) {
  return value.trim().toLocaleLowerCase();
}

export function useHardConfirm() {
  const { t } = useAppPreferences();
  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [hasAcknowledged, setHasAcknowledged] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pendingRequestRef = useRef<PendingRequest | null>(null);
  const titleId = useId();
  const descriptionId = useId();

  const resetDialog = () => {
    pendingRequestRef.current = null;
    setPendingRequest(null);
    setConfirmationInput("");
    setHasAcknowledged(false);
  };

  const closeDialog = (result: boolean) => {
    const activeRequest = pendingRequestRef.current;
    if (!activeRequest) {
      return;
    }

    activeRequest.resolve(result);
    resetDialog();
  };

  useEffect(() => {
    pendingRequestRef.current = pendingRequest;
  }, [pendingRequest]);

  useEffect(() => {
    return () => {
      pendingRequestRef.current?.resolve(false);
    };
  }, []);

  const confirm = (request: HardConfirmRequest) => {
    if (pendingRequestRef.current) {
      pendingRequestRef.current.resolve(false);
    }

    setConfirmationInput("");
    setHasAcknowledged(false);

    return new Promise<boolean>((resolve) => {
      const nextRequest = {
        ...request,
        resolve,
      };
      pendingRequestRef.current = nextRequest;
      setPendingRequest(nextRequest);
    });
  };

  const isConfirmationMatch = pendingRequest
    ? normalizeConfirmationValue(confirmationInput) ===
      normalizeConfirmationValue(pendingRequest.confirmationValue)
    : false;

  const dialog = pendingRequest ? (
    // The edit-venue form is already a Radix modal. A second Radix layer
    // suspends its focus trap while this confirmation is on top.
    <DialogPrimitive.Root
      onOpenChange={(open) => { if (!open) closeDialog(false); }}
      open
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          className="ops-dialog-backdrop"
          onClick={(event) => { if (event.target === event.currentTarget) closeDialog(false); }}
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            inputRef.current?.focus();
          }}
        >
          <div className="ops-dialog">
            <div className="ops-dialog-header">
              <p className="ops-kicker">{t("Hard confirmation")}</p>
              <DialogPrimitive.Title className="ops-dialog-title" id={titleId}>{pendingRequest.title}</DialogPrimitive.Title>
              <DialogPrimitive.Description className="ops-copy" id={descriptionId}>{pendingRequest.description}</DialogPrimitive.Description>
              {pendingRequest.consequence ? (
                <div className="ops-banner" data-tone="danger" role="status">
                  {pendingRequest.consequence}
                </div>
              ) : null}
            </div>

            <div className="ops-dialog-body">
              <label className="ops-checkbox-row ops-dialog-acknowledgement">
                <input
                  checked={hasAcknowledged}
                  onChange={(event) => setHasAcknowledged(event.target.checked)}
                  type="checkbox"
                />
                <span>{t("I understand this action is destructive and should not be one click.")}</span>
              </label>

              <label className="ops-field">
                <span className="ops-label">
                  {pendingRequest.confirmFieldLabel ?? t("Type {value} to confirm", { value: pendingRequest.confirmationValue })}
                </span>
                <input
                  autoCapitalize="off"
                  autoCorrect="off"
                  className="ops-input"
                  onChange={(event) => setConfirmationInput(event.target.value)}
                  placeholder={pendingRequest.confirmationValue}
                  ref={inputRef}
                  spellCheck={false}
                  value={confirmationInput}
                />
                <span className="ops-copy ops-dialog-help text-xs">
                  {t("Enter {value} exactly to enable the destructive action.", { value: pendingRequest.confirmationValue })}
                </span>
              </label>
            </div>

            <div className="ops-dialog-actions">
              <button
                className="ops-button"
                data-variant="secondary"
                onClick={() => closeDialog(false)}
                type="button"
              >
                {t("Cancel")}
              </button>
              <button
                className="ops-button"
                data-variant="danger"
                disabled={!hasAcknowledged || !isConfirmationMatch}
                onClick={() => closeDialog(true)}
                type="button"
              >
                {pendingRequest.confirmButtonLabel}
              </button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  ) : null;

  return {
    confirm,
    dialog,
  };
}
